// @storybook-internal widget renderer catalogued through PlacedWidgetItem.
import React, { useMemo, useState } from "react";
import "./widget-cards.css";

import {
  CHAT_V8_STOCK_WIDGET_INTERVALS,
  type T_ChatV8StockWidgetData,
  type T_ChatV8StockWidgetInterval,
} from "@lattice/core-lib/types/chat-v8";
import {
  buildChatV8StockPriceChartJson,
  formatStockWidgetDividendYield,
  formatStockWidgetMarketCap,
  formatStockWidgetRatio,
} from "@lattice/shared-frontend/chat-v8";

import { ChartRenderer } from "@lattice/frontend/components/charts-kit/ChartRenderer";
import { validateChartData } from "@lattice/frontend/components/charts-kit/chart-data";
import { IntervalPills } from "@lattice/frontend/components/charts-kit/IntervalPills";

export type T_StockWidgetCardProps = {
  data: T_ChatV8StockWidgetData;
  initialInterval?: T_ChatV8StockWidgetInterval;
  isStreaming: boolean;
};

/** Web mirror of the mobile StockWidgetCard: price header, chart, interval
 * pills BELOW the chart per policy, metric grid. */
export function StockWidgetCard({
  data,
  initialInterval = "1Y",
  isStreaming: _isStreaming,
}: T_StockWidgetCardProps) {
  const [interval, setInterval] =
    useState<T_ChatV8StockWidgetInterval>(initialInterval);
  const chartResult = useMemo(() => {
    const json = buildChatV8StockPriceChartJson({
      bars: data.intervals[interval],
      interval,
    });
    return json === null ? null : validateChartData(json);
  }, [data.intervals, interval]);

  return (
    <div className="cw-plain">
      <div className="cw-stock-header">
        <div className="cw-stock-identity">
          <StockLogo data={data} />
          <div>
            <p className="cw-stock-symbol">
              {data.symbol}
              {data.exchange ? (
                <span className="cw-stock-exchange">{data.exchange}</span>
              ) : null}
            </p>
            {data.name ? <p className="cw-stock-name">{data.name}</p> : null}
          </div>
        </div>
      </div>

      {chartResult?.success === true ? (
        <ChartRenderer
          chart={chartResult.data}
          trendColoring
          valueName={data.symbol}
        />
      ) : (
        <p className="cw-muted">Chart unavailable for this interval.</p>
      )}

      <IntervalPills
        active={interval}
        intervals={CHAT_V8_STOCK_WIDGET_INTERVALS}
        onChange={setInterval}
      />

      <div className="cw-metric-grid">
        {buildMetricEntries(data).map((metric) => (
          <div className="cw-metric" key={metric.label}>
            <p className="cw-metric-label">{metric.label}</p>
            <p className="cw-metric-value">{metric.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

type T_MetricEntry = { label: string; value: string };

function StockLogo({ data }: { data: T_ChatV8StockWidgetData }) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(data.image) && !failed;
  return (
    <span
      aria-label={`${data.name ?? data.symbol} logo`}
      className="cw-stock-logo"
    >
      {showImage && data.image ? (
        <img
          alt=""
          className="cw-stock-logo-image"
          onError={() => setFailed(true)}
          src={data.image}
        />
      ) : (
        (data.name ?? data.symbol).charAt(0).toUpperCase()
      )}
    </span>
  );
}

/** Mirrors mobile's StockWidgetMetricGrid rows exactly. */
function buildMetricEntries(data: T_ChatV8StockWidgetData): T_MetricEntry[] {
  return [
    {
      label: "Market cap",
      value: formatStockWidgetMarketCap(data.metrics.marketCap),
    },
    { label: "P/E", value: formatStockWidgetRatio(data.metrics.peRatio) },
    { label: "Beta", value: formatStockWidgetRatio(data.metrics.beta) },
    {
      label: "Dividend",
      value: formatStockWidgetDividendYield(data.metrics.dividendYield),
    },
  ].flatMap((row) =>
    row.value === null ? [] : [{ label: row.label, value: row.value }],
  );
}
